/**
 * Complementary Cart-Upsell (Search & Discovery): Anchor aus Add-to-Cart,
 * Sticky-Liste bei leerer Response, Filter nach Warenkorb-Varianten & Verfügbarkeit, Quick-Add.
 *
 * Anker bleibt auf PDP-Produkt (resolveAnchorProductId), auch wenn BOGOS weitere Zeilen hinzufügt.
 * postCartSync re-rendert nach cart:change / Drawer-Replace die Sticky-Liste.
 */
(function () {
	if (window.__cartComplementaryUpsellBound) return;
	window.__cartComplementaryUpsellBound = true;

	const FETCH_DEBOUNCE_MS = 320;
	const RECOMMENDATIONS_FETCH_LIMIT = 20;
	const POST_CART_SYNC_MS = 480;

	const state = {
		anchorProductId: null,
		lastSuccessfulProducts: [],
		lastSuccessfulAnchorId: null,
		debounceTimer: null,
		inFlight: null,
		inFlightProductId: null,
		inFlightPromise: null,
		postCartSyncTimer: null,
		isRenderingUpsell: false,
	};

	function firstCartLineProductId(cart) {
		const items = cart && cart.items;
		if (!items || !items.length) return null;
		const line = items[0];
		return line && line.product_id != null ? String(line.product_id) : null;
	}

	function lastCartLineProductId(cart) {
		const items = cart && cart.items;
		if (!items || !items.length) return null;
		const line = items[items.length - 1];
		return line && line.product_id != null ? String(line.product_id) : null;
	}

	function cartHasProductId(cart, productId) {
		if (!productId || !cart || !cart.items) return false;
		return cart.items.some(
			(line) => line && String(line.product_id) === String(productId)
		);
	}

	function resolveAnchorProductId(cart, anchorOverride) {
		if (anchorOverride != null) return String(anchorOverride);
		if (state.anchorProductId && cartHasProductId(cart, state.anchorProductId)) {
			return String(state.anchorProductId);
		}
		if (
			state.lastSuccessfulAnchorId &&
			cartHasProductId(cart, state.lastSuccessfulAnchorId)
		) {
			return String(state.lastSuccessfulAnchorId);
		}
		const items = cart && cart.items;
		if (items && items.length > 1) {
			const fromFirst = firstCartLineProductId(cart);
			if (fromFirst) return String(fromFirst);
		}
		const fromLast = lastCartLineProductId(cart);
		return fromLast ? String(fromLast) : null;
	}

	function readRootConfig(root) {
		return {
			recommendationsBase:
				root.dataset.recommendationsBase ||
				(window.routes && window.routes.product_recommendations_url) ||
				'',
			dataSectionId: root.dataset.dataSectionId || 'cart-complementary-data',
			displayLimit: Math.max(1, parseInt(root.dataset.limit || '4', 10) || 4),
			heading: root.dataset.heading || '',
			currency: root.dataset.currencyCode || 'EUR',
			strings: {
				add: root.dataset.stringAdd || 'Add',
				error: root.dataset.stringError || 'Error',
			},
		};
	}

	function getRoots() {
		return document.querySelectorAll('.js-cart-complementary-upsell');
	}

	function bindUpsellShellObserver(root) {
		if (!root || root.__upsellObserved) return;
		root.__upsellObserved = true;
		const obs = new MutationObserver(() => {
			if (state.isRenderingUpsell || !state.lastSuccessfulProducts.length) return;
			const listEmpty = !root.querySelector('[data-list] li');
			const isHidden = root.hasAttribute('hidden');
			if (!listEmpty && !isHidden) return;
			schedulePostCartSync();
		});
		obs.observe(root, {
			attributes: true,
			attributeFilter: ['hidden'],
			subtree: true,
			childList: true,
		});
	}

	function observeUpsellRoots() {
		getRoots().forEach(bindUpsellShellObserver);
	}

	function formatMoney(cents, currency) {
		const n = Number(cents);
		if (Number.isNaN(n)) return '';
		try {
			return new Intl.NumberFormat(document.documentElement.lang || 'de', {
				style: 'currency',
				currency: currency || 'EUR',
			}).format(n / 100);
		} catch (e) {
			return (n / 100).toFixed(2);
		}
	}

	function pickQuickAddVariant(product) {
		const variants = product.variants || [];
		const available = variants.filter((v) => v && v.available);
		if (!available.length) return null;
		return available[0];
	}

	function productHasAnyVariantInCart(product, inCart) {
		return (product.variants || []).some(
			(v) => v && inCart.has(String(v.id))
		);
	}

	function cartVariantIdSet(cart) {
		const set = new Set();
		(cart && cart.items ? cart.items : []).forEach((item) => {
			if (item && item.variant_id != null) set.add(String(item.variant_id));
		});
		return set;
	}

	function filterByCartAndStock(products, cart) {
		const inCart = cartVariantIdSet(cart);
		return (products || []).filter((p) => {
			if (productHasAnyVariantInCart(p, inCart)) return false;
			return pickQuickAddVariant(p) != null;
		});
	}

	function buildRecommendationsUrl(base, productId, dataSectionId) {
		const u = new URL(base, window.location.origin);
		u.searchParams.set('section_id', dataSectionId);
		u.searchParams.set('product_id', String(productId));
		u.searchParams.set('limit', String(RECOMMENDATIONS_FETCH_LIMIT));
		u.searchParams.set('intent', 'complementary');
		return u.toString();
	}

	async function fetchComplementaryProducts(productId, cfg) {
		if (state.inFlight && state.inFlightProductId !== String(productId)) {
			state.inFlight.abort();
		} else if (state.inFlight && state.inFlightProductId === String(productId)) {
			return state.inFlightPromise || [];
		}
		const ctrl = new AbortController();
		state.inFlight = ctrl;
		state.inFlightProductId = String(productId);
		const url = buildRecommendationsUrl(
			cfg.recommendationsBase,
			productId,
			cfg.dataSectionId
		);
		const fetchPromise = (async () => {
			const res = await fetch(url, { signal: ctrl.signal });
			if (!res.ok) throw new Error('fetch failed');
			const html = await res.text();
			const doc = new DOMParser().parseFromString(html, 'text/html');
			const jsonNode = doc.querySelector('[data-cart-complementary-json]');
			if (!jsonNode || !jsonNode.textContent) return [];
			try {
				const data = JSON.parse(jsonNode.textContent.trim());
				return Array.isArray(data) ? data : [];
			} catch (e) {
				return [];
			}
		})();
		state.inFlightPromise = fetchPromise;
		try {
			return await fetchPromise;
		} finally {
			if (state.inFlight === ctrl) {
				state.inFlight = null;
				state.inFlightProductId = null;
				state.inFlightPromise = null;
			}
		}
	}

	function setLive(root, message) {
		const live = root.querySelector('[data-live]');
		if (live) live.textContent = message || '';
	}

	function appendAddButtonContent(btn, cfg, productTitle, root) {
		const label = document.createElement('span');
		label.className = 'visually-hidden';
		label.textContent = `${cfg.strings.add}: ${(productTitle || '').replace(/</g, '')}`;
		btn.appendChild(label);

		const tpl = root.querySelector('.js-cart-complementary-add-icon-template');
		if (tpl && tpl.content && tpl.content.firstElementChild) {
			btn.appendChild(tpl.content.firstElementChild.cloneNode(true));
			return;
		}

		btn.insertAdjacentHTML(
			'beforeend',
			'<svg class="icon" aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 1v12M1 7h12" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>'
		);
	}

	function renderRows(root, products, cfg, cart) {
		const list = root.querySelector('[data-list]');
		const headingSlot = root.querySelector('[data-heading-slot]');
		if (!list || !headingSlot) return;

		state.isRenderingUpsell = true;
		try {
			headingSlot.textContent = cfg.heading || '';

			const filtered = filterByCartAndStock(products, cart).slice(0, cfg.displayLimit);
			list.innerHTML = '';

			if (!filtered.length) {
				root.setAttribute('hidden', 'hidden');
				setLive(root, '');
				return;
			}

			root.removeAttribute('hidden');

			const thumbSize = 50;

			filtered.forEach((product) => {
				const variant = pickQuickAddVariant(product);
				if (!variant) return;

				const li = document.createElement('li');
				li.className = 'cart-complementary-upsell__row';

				const thumb = document.createElement('div');
				thumb.className = 'cart-complementary-upsell__thumb';
				let imgSrc = null;
				if (typeof product.featured_image === 'string') {
					imgSrc = product.featured_image;
				} else if (product.featured_image && product.featured_image.src) {
					imgSrc = product.featured_image.src;
				} else if (
					product.featured_media &&
					product.featured_media.preview_image &&
					product.featured_media.preview_image.src
				) {
					imgSrc = product.featured_media.preview_image.src;
				} else if (product.image && product.image.src) {
					imgSrc = product.image.src;
				}
				if (imgSrc) {
					const im = document.createElement('img');
					im.src = imgSrc;
					im.alt = product.title || '';
					im.loading = 'lazy';
					im.width = thumbSize * 2;
					im.height = thumbSize * 2;
					thumb.appendChild(im);
				}
				li.appendChild(thumb);

				const mid = document.createElement('div');
				mid.className = 'cart-complementary-upsell__content';
				const title = document.createElement('p');
				title.className = 'cart-complementary-upsell__title';
				const link = document.createElement('a');
				link.href = product.url || '#';
				link.textContent = product.title || '';
				title.appendChild(link);
				mid.appendChild(title);

				const priceRow = document.createElement('p');
				priceRow.className = 'cart-complementary-upsell__price';
				const compare = variant.compare_at_price;
				const price = variant.price;
				const onSale = compare && Number(compare) > Number(price);
				if (onSale) {
					const saleSpan = document.createElement('span');
					saleSpan.className = 'cart-complementary-upsell__price-sale';
					saleSpan.textContent = formatMoney(price, cfg.currency);
					priceRow.appendChild(saleSpan);
					const compareEl = document.createElement('s');
					compareEl.className = 'cart-complementary-upsell__price-compare';
					compareEl.textContent = formatMoney(compare, cfg.currency);
					priceRow.appendChild(compareEl);
				} else {
					const span = document.createElement('span');
					span.textContent = formatMoney(price, cfg.currency);
					priceRow.appendChild(span);
				}
				mid.appendChild(priceRow);
				li.appendChild(mid);

				const btn = document.createElement('button');
				btn.type = 'button';
				btn.className = 'cart-complementary-upsell__add-btn';
				btn.setAttribute(
					'aria-label',
					`${cfg.strings.add}: ${(product.title || '').replace(/"/g, '')}`
				);
				btn.dataset.variantId = String(variant.id);
				appendAddButtonContent(btn, cfg, product.title, root);
				btn.addEventListener('click', () => quickAddVariant(btn, variant.id, cfg));
				li.appendChild(btn);

				list.appendChild(li);
			});

			setLive(root, '');
		} finally {
			state.isRenderingUpsell = false;
		}
	}

	function getSectionsParamAndUrl() {
		const drawer = document.querySelector('cart-drawer');
		if (drawer && typeof drawer.getSectionsToRender === 'function') {
			return {
				sections: drawer.getSectionsToRender().map((s) => s.id).join(','),
				sectionsUrl: window.location.pathname,
				mode: 'drawer',
			};
		}
		const cartItems = document.querySelector('cart-items');
		if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
			return {
				sections: cartItems.getSectionsToRender().map((s) => s.section).join(','),
				sectionsUrl: window.location.pathname,
				mode: 'page',
			};
		}
		return { sections: '', sectionsUrl: window.location.pathname, mode: 'none' };
	}

	function refreshMainCartFromResponse(parsedState) {
		const cartItems = document.querySelector('cart-items');
		if (!cartItems || !parsedState.sections) return;
		cartItems.getSectionsToRender().forEach((section) => {
			const block =
				document.getElementById(section.id)?.querySelector(section.selector) ||
				document.getElementById(section.id);
			const html = parsedState.sections[section.section];
			if (block && html) {
				block.innerHTML = cartItems.getSectionInnerHTML(html, section.selector);
			}
		});
		const cartDrawerWrapper = document.querySelector('cart-drawer');
		const cartFooter = document.getElementById('main-cart-footer');
		const count =
			parsedState.item_count != null
				? Number(parsedState.item_count)
				: (parsedState.items && parsedState.items.length) || 0;
		if (cartFooter) cartFooter.classList.toggle('is-empty', count === 0);
		if (cartDrawerWrapper)
			cartDrawerWrapper.classList.toggle('is-empty', count === 0);
		cartItems.classList.toggle('is-empty', count === 0);
		cartItems.classList.toggle('is-full', count > 0);
		if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
			publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
		}
	}

	async function quickAddVariant(button, variantId, cfg) {
		const { sections, sectionsUrl, mode } = getSectionsParamAndUrl();
		button.classList.add('is-loading');
		button.setAttribute('aria-disabled', 'true');

		const payload = {
			items: [{ id: variantId, quantity: 1 }],
			sections_url: sectionsUrl,
		};
		if (sections) payload.sections = sections;

		try {
			const res = await fetch(window.routes.cart_add_url, {
				...fetchConfig(),
				body: JSON.stringify(payload),
			});
			const parsed = await res.json();
			if (parsed.status) {
				throw new Error(parsed.description || parsed.message || 'cart error');
			}

			const lines = parsed.items || [];
			const match = lines.find(
				(it) => String(it.variant_id) === String(variantId)
			);
			const fallback = lines.length ? lines[lines.length - 1] : null;
			const line = match || fallback;
			const newAnchor =
				line && line.product_id != null ? String(line.product_id) : null;
			if (newAnchor) state.anchorProductId = newAnchor;

			const drawer = document.querySelector('cart-drawer');
			if (drawer && typeof drawer.renderContents === 'function' && mode === 'drawer') {
				drawer.renderContents(parsed);
			} else {
				refreshMainCartFromResponse(parsed);
			}

			initHeadingText();

			let cartForUi = parsed;
			try {
				cartForUi = await fetch(`${window.routes.cart_url}.js`).then((r) =>
					r.json()
				);
			} catch (e) {
				/* parsed bleibt Fallback */
			}

			document.dispatchEvent(
				new CustomEvent('cart:change', {
					detail: { cart: cartForUi, sectionId: 'cart-complementary-upsell' },
				})
			);

			const rootsAfter = getRoots();
			const cfgNext =
				rootsAfter.length > 0 ? readRootConfig(rootsAfter[0]) : cfg;
			if (newAnchor) {
				await runFetchForAnchor(newAnchor, cfgNext, cartForUi);
			} else {
				rootsAfter.forEach((root) => {
					renderRows(
						root,
						state.lastSuccessfulProducts,
						readRootConfig(root),
						cartForUi
					);
				});
			}
		} catch (e) {
			console.error(e);
			setLive(button.closest('.js-cart-complementary-upsell'), cfg.strings.error);
		} finally {
			button.classList.remove('is-loading');
			button.removeAttribute('aria-disabled');
		}
	}

	async function runFetchForAnchor(productId, cfg, cart) {
		if (!productId) return;
		try {
			const products = await fetchComplementaryProducts(productId, cfg);
			if (products.length > 0) {
				state.lastSuccessfulProducts = products;
				state.lastSuccessfulAnchorId = productId;
				state.anchorProductId = String(productId);
			}
			const source =
				products.length > 0 ? products : state.lastSuccessfulProducts;
			getRoots().forEach((root) => {
				renderRows(root, source, readRootConfig(root), cart || { items: [] });
				bindUpsellShellObserver(root);
			});
		} catch (e) {
			if (e.name === 'AbortError') {
				if (state.lastSuccessfulProducts.length) {
					getRoots().forEach((root) => {
						renderRows(
							root,
							state.lastSuccessfulProducts,
							readRootConfig(root),
							cart || { items: [] }
						);
					});
				}
				return;
			}
			console.error(e);
			getRoots().forEach((root) => {
				renderRows(
					root,
					state.lastSuccessfulProducts,
					readRootConfig(root),
					cart || { items: [] }
				);
			});
		}
	}

	function scheduleFetch(productId, cart) {
		clearTimeout(state.debounceTimer);
		state.debounceTimer = setTimeout(async () => {
			const roots = getRoots();
			if (!roots.length || !productId) return;
			const cfg = readRootConfig(roots[0]);
			await runFetchForAnchor(String(productId), cfg, cart);
		}, FETCH_DEBOUNCE_MS);
	}

	function onCartOrAnchorChange(cart, anchorOverride) {
		const roots = getRoots();
		if (!roots.length) return;

		const empty =
			!cart ||
			(cart.item_count != null && Number(cart.item_count) === 0) ||
			(!cart.items || cart.items.length === 0);
		if (empty) {
			state.anchorProductId = null;
			state.lastSuccessfulProducts = [];
			state.lastSuccessfulAnchorId = null;
			roots.forEach((r) => {
				r.setAttribute('hidden', 'hidden');
				const list = r.querySelector('[data-list]');
				if (list) list.innerHTML = '';
			});
			return;
		}

		if (anchorOverride != null) {
			state.anchorProductId = String(anchorOverride);
		} else {
			const resolved = resolveAnchorProductId(cart, null);
			if (resolved) state.anchorProductId = resolved;
		}

		const freshRoots = getRoots();
		const pid =
			resolveAnchorProductId(cart, anchorOverride) ||
			(freshRoots[0] && freshRoots[0].dataset.initialAnchorProductId) ||
			null;
		if (!pid) {
			freshRoots.forEach((root) => {
				renderRows(root, state.lastSuccessfulProducts, readRootConfig(root), cart);
			});
			return;
		}

		if (
			state.lastSuccessfulAnchorId === String(pid) &&
			state.lastSuccessfulProducts.length
		) {
			freshRoots.forEach((root) => {
				renderRows(root, state.lastSuccessfulProducts, readRootConfig(root), cart);
			});
			return;
		}

		scheduleFetch(String(pid), cart);
	}

	function cartFromParsedState(parsed) {
		if (!parsed) return { items: [] };
		return {
			item_count: parsed.item_count,
			items: parsed.items || [],
		};
	}

	async function refreshAfterDrawerContentsUpdated(parsedState) {
		const cart = cartFromParsedState(parsedState);
		if (!cart.items || !cart.items.length) return;

		const roots = getRoots();
		if (!roots.length) return;

		const pid =
			resolveAnchorProductId(cart, state.anchorProductId) ||
			resolveAnchorProductId(cart, null) ||
			roots[0].dataset.initialAnchorProductId ||
			null;
		if (!pid) return;

		state.anchorProductId = String(pid);
		clearTimeout(state.debounceTimer);

		const cfg = readRootConfig(roots[0]);
		await runFetchForAnchor(String(pid), cfg, cart);
		schedulePostCartSync();
	}

	function schedulePostCartSync() {
		clearTimeout(state.postCartSyncTimer);
		state.postCartSyncTimer = setTimeout(async () => {
			const roots = getRoots();
			if (!roots.length || typeof fetch !== 'function' || !window.routes) return;

			let cart;
			try {
				cart = await fetch(`${window.routes.cart_url}.js`).then((r) => r.json());
			} catch (e) {
				return;
			}

			if (!cart.items || !cart.items.length) {
				onCartOrAnchorChange(cart, null);
				return;
			}

			const pid = resolveAnchorProductId(cart, null);
			if (!pid) return;
			state.anchorProductId = String(pid);

			if (
				state.lastSuccessfulAnchorId === String(pid) &&
				state.lastSuccessfulProducts.length
			) {
				roots.forEach((root) => {
					renderRows(
						root,
						state.lastSuccessfulProducts,
						readRootConfig(root),
						cart
					);
				});
				return;
			}
			await runFetchForAnchor(String(pid), readRootConfig(roots[0]), cart);
		}, POST_CART_SYNC_MS);
	}

	function initHeadingText() {
		getRoots().forEach((root) => {
			const cfg = readRootConfig(root);
			const slot = root.querySelector('[data-heading-slot]');
			if (slot) slot.textContent = cfg.heading;
		});
	}

	document.addEventListener('DOMContentLoaded', () => {
		initHeadingText();
		const roots = getRoots();
		if (!roots.length) return;
		if (typeof fetchConfig !== 'function' || !window.routes || !window.routes.cart_add_url) {
			return;
		}

		fetch(`${window.routes.cart_url}.js`)
			.then((r) => r.json())
			.then((cart) => {
				const anchor =
					state.anchorProductId ||
					lastCartLineProductId(cart) ||
					roots[0].dataset.initialAnchorProductId ||
					null;
				if (anchor) {
					state.anchorProductId = String(anchor);
					onCartOrAnchorChange(cart, state.anchorProductId);
				} else {
					roots.forEach((r) => r.setAttribute('hidden', 'hidden'));
				}
			})
			.catch(() => {});

		observeUpsellRoots();

		if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
			subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
				if (event.source === 'cart-items') return;
				schedulePostCartSync();
			});
		}
	});

	document.addEventListener('cart-drawer:contents-updated', (event) => {
		const parsed = event.detail && event.detail.parsedState;
		if (!parsed) return;
		observeUpsellRoots();
		refreshAfterDrawerContentsUpdated(parsed);
	});

	document.addEventListener('variant:add', (event) => {
		if (!getRoots().length) return;
		const detail = event.detail || {};
		const productId =
			detail.productId ||
			detail.product_id ||
			(detail.product && detail.product.id);
		if (productId) state.anchorProductId = String(productId);
	});

	document.addEventListener('cart:change', (event) => {
		const cart = event.detail && event.detail.cart;
		const sectionId = event.detail && event.detail.sectionId;
		if (!cart) return;

		if (sectionId === 'cart-complementary-upsell') return;

		setTimeout(() => {
			if (!getRoots().length) return;
			schedulePostCartSync();
		}, 0);
	});
})();
